/**
 * No Review Items Empty State
 * 
 * Displayed when there are no transactions to review (success state).
 * AI has classified everything with high confidence.
 * 
 * @see UX_ACTION_PLAN_ENHANCED_2025-10-13.md - Fix 5
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

interface NoReviewItemsEmptyStateProps {
  totalTransactions?: number;
  aiConfidenceAvg?: number;
}

export const NoReviewItemsEmptyState = ({ 
  totalTransactions = 0,
  aiConfidenceAvg = 95 
}: NoReviewItemsEmptyStateProps) => {
  const navigate = useNavigate();

  return (
    <Card className="border-brand-success bg-validation-low-bg rounded-2xl">
      <CardHeader className="text-center pb-4">
        <div className="w-20 h-20 mx-auto mb-4 bg-white border-4 border-brand-success rounded-2xl flex items-center justify-center">
          <i className="fa-solid fa-robot text-4xl text-brand-success"></i>
        </div>
        <CardTitle className="text-2xl font-space-grotesk text-brand-primary">
          AI Did All The Work! ✨
        </CardTitle>
        <p className="text-sm text-brand-text-secondary mt-2 max-w-md mx-auto">
          {totalTransactions > 0 
            ? `All ${totalTransactions} transactions were classified with ${aiConfidenceAvg}% average confidence. No review needed!`
            : 'All transactions have been automatically classified with high confidence.'
          }
        </p>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* AI Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-white">
            <CardContent className="pt-4 pb-4 text-center">
              <div className="text-2xl font-bold font-space-grotesk text-brand-success">
                {totalTransactions}
              </div>
              <div className="text-xs text-brand-text-secondary mt-1">
                Auto-Classified
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="pt-4 pb-4 text-center">
              <div className="text-2xl font-bold font-space-grotesk text-brand-success">
                {aiConfidenceAvg}%
              </div>
              <div className="text-xs text-brand-text-secondary mt-1">
                Confidence
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="pt-4 pb-4 text-center">
              <div className="text-2xl font-bold font-space-grotesk text-brand-success">
                0
              </div>
              <div className="text-xs text-brand-text-secondary mt-1">
                Need Review
              </div>
            </CardContent>
          </Card>
        </div>

        {/* How AI Works */}
        <Card className="bg-blue-50 border-blue-200 rounded-xl">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-start gap-3">
              <i className="fa-solid fa-brain text-xl text-blue-600 flex-shrink-0"></i>
              <div>
                <h4 className="font-semibold text-brand-primary text-sm mb-2">
                  How Our AI Classified These
                </h4>
                <ul className="space-y-1 text-xs text-brand-text-secondary">
                  <li>✓ Pattern matching from historical data</li>
                  <li>✓ Swiss GAAP account mapping</li>
                  <li>✓ Automatic 8.1% VAT calculation</li>
                  <li>✓ Counterparty recognition</li>
                  <li>✓ Learning from your corrections</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-3">
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => navigate('/transactions')}
          >
            <i className="fa-solid fa-eye mr-2"></i>
            View All Transactions
          </Button>
          <Button 
            className="w-full"
            onClick={() => navigate('/validation')}
          >
            <i className="fa-solid fa-check mr-2"></i>
            Validate & Approve
          </Button>
        </div>

        {/* Next Steps */}
        <div className="text-center">
          <p className="text-xs text-brand-text-secondary mb-2">
            Ready for the next step?
          </p>
          <Button 
            variant="link" 
            onClick={() => navigate('/workspace')}
            className="text-brand-primary"
          >
            Go to Workspace →
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
